import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';

import {
  DEMO_COPY,
  FIELD_LABELS,
  STEP_LABELS,
  STEP_ORDER,
  type DemoField,
} from './VoiceAgentDemo.content';
import {
  MOMENTS,
  type DemoMessage,
  type DemoMoment,
  type MomentId,
} from './VoiceAgentDemo.fixtures';

// A rebuild of Jiameng's voice agent demo (the LiveKit page), for designing
// what comes next from it. Same layout and words as the demo, drawn in our
// tokens rather than its Tailwind slate and teal. Not interactive: each moment
// is a still from one test call.

const FIELDS = Object.keys(FIELD_LABELS) as DemoField[];

const Bubble = ({ role, text }: DemoMessage) => (
  <Box
    sx={{
      alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
      maxWidth: '80%',
      px: 2,
      py: 1.5,
      borderRadius: 2,
      bgcolor: role === 'user' ? 'custom.marineGreen.500' : 'custom.neutral.100',
    }}
  >
    <Typography
      variant='body2'
      sx={{ color: role === 'user' ? 'common.white' : 'text.primary' }}
    >
      {text}
    </Typography>
  </Box>
);

const VoiceAgentDemo = ({ moment }: { moment: MomentId }) => {
  const m: DemoMoment = MOMENTS[moment];
  const live = m.status !== 'Offline';
  const current = m.step ? STEP_ORDER.indexOf(m.step) : -1;

  return (
    <Box sx={{ height: '100%', bgcolor: 'background.default', p: 5 }}>
      <Stack spacing={2} sx={{ height: '100%', maxWidth: 1120, mx: 'auto' }}>
        <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: live ? 'custom.marineGreen.500' : 'custom.neutral.300',
              }}
            />
            <Typography variant='body2'>{m.status}</Typography>
          </Stack>
          <Button variant={live ? 'outlined' : 'contained'} size='small'>
            {live ? DEMO_COPY.endCall : DEMO_COPY.startCall}
          </Button>
        </Stack>

        <Stack direction='row' spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Paper
              variant='outlined'
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: m.messages.length ? 'flex-end' : 'center',
                gap: 1.5,
              }}
            >
              {m.messages.length ? (
                m.messages.map((msg, i) => <Bubble key={i} {...msg} />)
              ) : (
                <Typography variant='body2' sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  {DEMO_COPY.idle}
                </Typography>
              )}
            </Paper>
            <Paper variant='outlined' sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {live ? DEMO_COPY.inputLive : DEMO_COPY.inputIdle}
              </Typography>
            </Paper>
          </Stack>

          {m.step ? (
            <Paper variant='outlined' sx={{ width: 380, flexShrink: 0, overflow: 'hidden' }}>
              <Stack spacing={1.5} sx={{ p: 2.5, borderBottom: 1, borderColor: 'custom.neutral.200' }}>
                <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>{DEMO_COPY.title}</Typography>
                  <Chip size='small' label={STEP_LABELS[m.step]} />
                </Stack>
                <Stack direction='row' spacing={0.5}>
                  {STEP_ORDER.map((s, i) => (
                    <Box
                      key={s}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 1,
                        bgcolor:
                          i < current
                            ? 'custom.marineGreen.500'
                            : i === current
                              ? 'custom.limeGreen.500'
                              : 'custom.neutral.200',
                      }}
                    />
                  ))}
                </Stack>
                {m.ended ? (
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {DEMO_COPY.ended}
                  </Typography>
                ) : null}
              </Stack>
              <Stack sx={{ px: 2.5 }}>
                {FIELDS.map((f) => {
                  const value = m.answers[f];
                  const stale = m.stale === f;
                  return (
                    <Stack
                      key={f}
                      spacing={0.25}
                      sx={{
                        py: 1.25,
                        borderBottom: 1,
                        borderColor: 'custom.neutral.200',
                        ...(stale && {
                          mx: -1,
                          px: 1,
                          border: 2,
                          borderRadius: 1,
                          borderColor: 'custom.cherry.500',
                        }),
                      }}
                    >
                      <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                        {FIELD_LABELS[f]}
                      </Typography>
                      <Typography variant='body2' sx={{ color: value ? 'text.primary' : 'text.disabled' }}>
                        {value ?? DEMO_COPY.notAnswered}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          ) : null}
        </Stack>

        {m.toast ? (
          <Box sx={{ alignSelf: 'center', px: 2, py: 1.5, borderRadius: 2, bgcolor: 'custom.charcoal' }}>
            <Typography variant='body2' sx={{ color: 'common.white' }}>
              {m.toast}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default VoiceAgentDemo;
